'use client'

import { ClientLabel } from 'labels/client'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { CONTENT_PLACEHOLDER } from 'my-purchases/constants/constants'
import { ORDER_TYPES } from 'my-purchases/constants/orderTypes'
import { PAYMENT_TYPES } from 'my-purchases/constants/paymentTypes'
import type { PurchaseDetails } from 'my-purchases/types/purchaseDetail'
import type { PurchaseStatusDetails } from 'my-purchases/types/purchaseStatus'
import { useMemo } from 'react'

import { SummaryLine } from './components/SummaryLine/SummaryLine'

import styles from './SummaryContainer.module.scss'
import texts from 'fukku/styles/texts.module.scss'

export function SummaryContainer({
	purchase,
}: {
	readonly purchase: PurchaseDetails | PurchaseStatusDetails
}) {
	const { t } = useLabels()

	const {
		hasFreeShipping,
		totalPriceAmount,
		subTotalAmount,
		totalPriceAmountConverted,
		subTotalAmountConverted,
		shippingPriceAmountConverted,
		isMulti,
		type,
		// Online orders props
		paidBalanceAmountValue,
		paidAmountValue,
		paidAmountValueConverted,
		paidBalanceAmountValueConverted,
	} = purchase

	/**
	 * Props only available when PurchaseDetails
	 */
	const taxAmountConverted =
		'taxAmountConverted' in purchase ? purchase.taxAmountConverted : undefined
	const paymentMethod =
		'paymentMethod' in purchase ? purchase.paymentMethod : undefined

	const showShipping = type !== ORDER_TYPES.STORE

	const showPaymentMethod = useMemo(
		() =>
			Boolean(
				!!paymentMethod &&
					paidBalanceAmountValue &&
					paidAmountValue &&
					PAYMENT_TYPES[paymentMethod as keyof typeof PAYMENT_TYPES]
			),
		[purchase]
	)

	/**
	 * Show subtotals only if all orders have it
	 */
	const showSubtotalByOrder = useMemo(
		() => isMulti && purchase.orders.every((order) => 'subTotal' in order),
		[purchase.orders, isMulti]
	)

	const showTotalPriceAmount = useMemo(
		() => !!totalPriceAmount && !Number.isNaN(totalPriceAmount),
		[totalPriceAmount]
	)

	return (
		<div className={styles.container}>
			<h2 className={`${texts.titleL} ${styles.headline}`}>
				{t('myPurchases.online.details.summary.title')}
			</h2>
			<div className={styles.lines}>
				{!!paidBalanceAmountValueConverted && (
					<SummaryLine
						data-testid='myPurchases.detail.paidBalanceWithStoreCredit'
						label={t('myPurchases.online.details.payments_storeCredit.text')}
						price={paidBalanceAmountValueConverted.price}
					/>
				)}
				{showPaymentMethod && (
					<SummaryLine
						data-testid='myPurchases.detail.paymentMethod'
						label={t(
							`myPurchases.online.details.payments_${PAYMENT_TYPES[paymentMethod as keyof typeof PAYMENT_TYPES]}.text`
						)}
						price={paidAmountValueConverted?.price ?? CONTENT_PLACEHOLDER}
					/>
				)}

				{!!subTotalAmount && !showSubtotalByOrder && (
					<SummaryLine
						data-testid='myPurchases.detail.subtotal'
						label={t('myPurchases.online.details.subtotalInfo.text')}
						price={subTotalAmountConverted.price}
					/>
				)}
				{showSubtotalByOrder && (
					<>
						{purchase.orders.map((order, idx) => (
							<SummaryLine
								key={`${order.id}_${purchase.orders.length - idx}`}
								data-testid='myPurchases.order.price'
								label={
									<ClientLabel
										placeholderValues={{
											1: idx + 1,
										}}
									>
										{t('shoppingBag.shipping.multiple.delivery.title')}
									</ClientLabel>
								}
								price={order.subTotal}
							/>
						))}
					</>
				)}
				{showShipping && (
					<SummaryLine
						data-testid='myPurchases.detail.shipping'
						label={t('my_purchases.order.label.shipping')}
						price={
							hasFreeShipping
								? t('my_purchases.order.shipping.free')
								: shippingPriceAmountConverted.price
						}
					/>
				)}
				{!!taxAmountConverted && (
					<SummaryLine
						data-testid='myPurchases.detail.tax'
						label={t('myPurchases.online.details.taxesInfo.text')}
						price={taxAmountConverted.price}
					/>
				)}
				{showTotalPriceAmount && (
					<SummaryLine
						className={texts.titleL}
						data-testid='myPurchases.detail.total'
						label={t('my_purchases.details.total')}
						price={totalPriceAmountConverted.price}
						additionalPrices={totalPriceAmountConverted.additionalPrices}
					/>
				)}
			</div>
		</div>
	)
}
